import { FC, ReactNode, useEffect } from 'react';
import { CgChevronDown } from 'react-icons/cg';
import AsyncSelect from 'react-select/async';
import { Box, Field } from '@chakra-ui/react';

import {
  Nullable,
  Option,
  SuggestionSelectOptionsProps,
} from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { FormLabel, FormLabelProps } from '../../shared/components/form';

type SuggestionSelectProps = {
  label: string;
  labelProps?: FormLabelProps;
  required?: boolean;
  name: string;
  onSelect: (e: SuggestionSelectOptionsProps) => void;

  options: Option[];
  value?: Nullable<string>;
  onReset: () => void;
  onPromise: (e: string) => SuggestionSelectOptionsProps[];
  placeholder?: ReactNode;
};

const DropdownIndicator = () => (
  <Box position="relative" right="11px">
    <CgChevronDown />
  </Box>
);

export const SuggestionSelect: FC<SuggestionSelectProps> = ({
  label,
  labelProps,
  required,
  name,
  onSelect,
  options,
  value,
  onReset,
  onPromise,
  placeholder,
}) => {
  const handleOnPromise = async (inputValue: any): Promise<any> => {
    try {
      onPromise(inputValue);

      if (options.length) {
        return options;
      }
    } catch (err: any) {
      handleError(err);
    }
    return undefined;
  };

  const handleOnSelect = (e: any) => {
    onSelect(e);
  };

  useEffect(() => {
    if (value === undefined) {
      onReset();
    }
  }, [value]);

  const selectedValue = options?.filter((option) => option.value === value)[0];

  const customStyles = {
    indicatorSeparator: () => ({
      display: 'none',
    }),
  };

  return (
    <Field.Root>
      {label && (
        <FormLabel required={required} {...labelProps}>
          {label}
        </FormLabel>
      )}

      <AsyncSelect
        components={{ DropdownIndicator }}
        defaultOptions
        loadOptions={handleOnPromise}
        name={name}
        onChange={(e) => {
          handleOnSelect(e);
        }}
        placeholder={placeholder}
        styles={customStyles}
        value={selectedValue || null}
      />
    </Field.Root>
  );
};
